import React, { useState, useMemo, useCallback } from 'react';
import { Compass, User, Clock, Zap, Database, FileBarChart, Sparkles, Pin, Check } from 'lucide-react';
import { ChatMessage, ResponseBlock } from '../types';
import { TextBlock } from './TextBlock';
import { TableBlock } from './TableBlock';
import { ChartBlock } from './ChartBlock';
import { KpiBlock } from './KpiBlock';
import { SqlBlock } from './SqlBlock';
import { FileBlock } from './FileBlock';
import { FunnelBlock } from './FunnelBlock';
import { HeatmapBlock } from './HeatmapBlock';
import { TreemapBlock } from './TreemapBlock';
import { type VizType } from './VizSwitcher';
import {
    tableDataToChartData,
    chartDataToTableData,
    suggestVizType,
    type VizSuggestion,
} from '../../../../../utils/chartUtils';

interface MessageBubbleProps {
    message: ChatMessage;
    generatedSQL?: string;
    onSaveAsReport?: (sql: string, prompt: string) => void;
    /** The user prompt that triggered this assistant response */
    userPrompt?: string;
    /** Callback for drill-down: sends a follow-up query to Compass */
    onDrillDown?: (query: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onSaveAsReport, userPrompt, onDrillDown }) => {
    const isUser = message.role === 'user';

    // VizSwitcher state: track per-block overrides
    const [vizOverrides, setVizOverrides] = useState<Record<number, VizType>>({});
    // Track which suggestion badges the user has dismissed
    const [dismissedSuggestions, setDismissedSuggestions] = useState<Record<number, boolean>>({});
    // Pin to dashboard state
    const [isPinning, setIsPinning] = useState(false);
    const [pinSuccess, setPinSuccess] = useState(false);

    // Compute data-shape suggestions for chart and table blocks
    const suggestions = useMemo(() => {
        const result: Record<number, VizSuggestion> = {};
        if (!message.blocks) return result;

        message.blocks.forEach((block, idx) => {
            if (block.type === 'chart' && block.chartData) {
                const flatData = block.chartData.series?.[0]?.data || [];
                if (flatData.length > 0) {
                    const suggestion = suggestVizType(flatData, undefined, block.chartData.chartType);
                    if (
                        suggestion.confidence !== 'low' &&
                        suggestion.suggestedType !== block.chartData.chartType?.toLowerCase()
                    ) {
                        result[idx] = suggestion;
                    }
                }
            } else if (block.type === 'table' && block.tableData) {
                const suggestion = suggestVizType(
                    block.tableData.rows,
                    block.tableData.columns,
                    'table'
                );
                if (suggestion.confidence !== 'low' && suggestion.suggestedType !== 'table') {
                    result[idx] = suggestion;
                }
            }
        });

        return result;
    }, [message.blocks]);

    const handleVizSwitch = (blockIdx: number, newType: VizType) => {
        setVizOverrides(prev => ({ ...prev, [blockIdx]: newType }));
        // Dismiss the suggestion if user manually switched
        setDismissedSuggestions(prev => ({ ...prev, [blockIdx]: true }));
    };

    /**
     * Pin this message's data to the WMS Dashboard as a compass-report widget.
     * First saves as a report (reusing the onSaveAsReport flow), then dispatches
     * a custom event that DashboardContext listens for.
     */
    const handlePinToDashboard = useCallback(async () => {
        if (!message.generatedSQL || isPinning) return;
        setIsPinning(true);

        try {
            // Import claudeQueryService dynamically to avoid circular deps
            const { claudeQueryService } = await import('../../../../../services/claudeQueryService');

            // Auto-create a saved report from the current SQL
            const report = await claudeQueryService.createReport({
                name: userPrompt?.slice(0, 60) || 'Compass-rapport',
                description: `Pinnet fra Compass: ${userPrompt || ''}`,
                generatedSQL: message.generatedSQL,
                originalPrompt: userPrompt || '',
                exportFormat: 'table',
                category: 'Dashboard',
            });

            // Determine display mode based on first data block
            let displayMode = 'table';
            const firstChartBlock = message.blocks?.find(b => b.type === 'chart');
            const firstKpiBlock = message.blocks?.find(b => b.type === 'kpi');
            if (firstChartBlock?.chartData) {
                displayMode = firstChartBlock.chartData.chartType || 'bar';
            } else if (firstKpiBlock) {
                displayMode = 'kpi';
            }

            // Dispatch event for DashboardContext to pick up
            window.dispatchEvent(new CustomEvent('compass-pin-to-dashboard', {
                detail: {
                    reportId: report.reportId,
                    displayMode,
                    title: userPrompt?.slice(0, 60) || 'Compass-rapport',
                },
            }));

            setPinSuccess(true);
            setTimeout(() => setPinSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to pin to dashboard:', error);
        } finally {
            setIsPinning(false);
        }
    }, [message.generatedSQL, message.blocks, userPrompt, isPinning]);

    const applySuggestion = (blockIdx: number, suggestedType: string) => {
        setVizOverrides(prev => ({ ...prev, [blockIdx]: suggestedType as VizType }));
        setDismissedSuggestions(prev => ({ ...prev, [blockIdx]: true }));
    };

    const renderBlock = (block: ResponseBlock, index: number) => {
        const override = vizOverrides[index];

        switch (block.type) {
            case 'text':
                return block.content ? <TextBlock key={index} content={block.content} /> : null;

            case 'table': {
                if (!block.tableData) return null;

                // If user switched to a chart type, render as chart
                if (override && override !== 'table') {
                    const chartData = tableDataToChartData(block.tableData, override);
                    return (
                        <ChartBlock
                            key={index}
                            data={chartData as any}
                            currentVizType={override}
                            onVizSwitch={(type) => handleVizSwitch(index, type)}
                            onDataPointClick={onDrillDown ? (label) => {
                                onDrillDown(`Vis detaljer for "${label}"`);
                            } : undefined}
                        />
                    );
                }

                return (
                    <React.Fragment key={index}>
                        <TableBlock
                            data={block.tableData}
                            generatedSQL={message.generatedSQL}
                            onVizSwitch={(type) => handleVizSwitch(index, type)}
                        />
                        {renderSuggestionBadge(index)}
                    </React.Fragment>
                );
            }

            case 'chart': {
                if (!block.chartData) return null;

                // If user switched to table, render as table
                if (override === 'table') {
                    const tableData = chartDataToTableData(block.chartData as any);
                    return (
                        <TableBlock
                            key={index}
                            data={tableData as any}
                            generatedSQL={message.generatedSQL}
                            onVizSwitch={(type) => handleVizSwitch(index, type)}
                        />
                    );
                }

                return (
                    <React.Fragment key={index}>
                        <ChartBlock
                            data={block.chartData}
                            currentVizType={override || (block.chartData.chartType?.toLowerCase() as VizType)}
                            onVizSwitch={(type) => handleVizSwitch(index, type)}
                            onDataPointClick={onDrillDown ? (label) => {
                                onDrillDown(`Vis detaljer for "${label}"`);
                            } : undefined}
                        />
                        {renderSuggestionBadge(index)}
                    </React.Fragment>
                );
            }

            case 'kpi':
                return block.kpiData ? <KpiBlock key={index} data={block.kpiData} /> : null;
            case 'file':
                return block.fileData ? <FileBlock key={index} data={block.fileData} /> : null;
            case 'sql':
                return block.content ? <SqlBlock key={index} sql={block.content} /> : null;

            case 'funnel':
                return block.funnelData ? (
                    <FunnelBlock
                        key={index}
                        data={block.funnelData}
                        onStepClick={onDrillDown ? (step) => {
                            onDrillDown(`Vis detaljer for ordrer med status "${step.label}"`);
                        } : undefined}
                    />
                ) : null;

            case 'heatmap':
                return block.heatmapData ? (
                    <HeatmapBlock
                        key={index}
                        data={block.heatmapData}
                        onCellClick={onDrillDown ? (cell) => {
                            onDrillDown(`Vis detaljer for lokasjon ${cell.code}`);
                        } : undefined}
                    />
                ) : null;

            case 'treemap':
                return block.treemapData ? (
                    <TreemapBlock
                        key={index}
                        data={block.treemapData}
                        onNodeClick={onDrillDown ? (node) => {
                            onDrillDown(`Vis detaljer for kategori "${node.name}"`);
                        } : undefined}
                    />
                ) : null;

            case 'error':
                return block.content ? (
                    <div key={index} className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        {block.content}
                    </div>
                ) : null;
            default:
                return null;
        }
    };

    const renderSuggestionBadge = (blockIdx: number) => {
        const suggestion = suggestions[blockIdx];
        if (!suggestion || dismissedSuggestions[blockIdx] || vizOverrides[blockIdx]) return null;

        const typeLabels: Record<string, string> = {
            kpi: 'KPI-kort',
            bar: 'Stolpediagram',
            line: 'Linjediagram',
            area: 'Omradediagram',
            pie: 'Kakediagram',
            table: 'Tabell',
        };

        return (
            <div className="flex items-center gap-2 mt-1">
                <button
                    onClick={() => applySuggestion(blockIdx, suggestion.suggestedType)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 transition-colors"
                >
                    <Sparkles size={11} />
                    Forslag: {typeLabels[suggestion.suggestedType] || suggestion.suggestedType}
                </button>
                <button
                    onClick={() => setDismissedSuggestions(prev => ({ ...prev, [blockIdx]: true }))}
                    className="text-xs text-gray-400 hover:text-gray-600"
                >
                    Avvis
                </button>
            </div>
        );
    };

    if (isUser) {
        return (
            <div className="flex justify-end">
                <div className="max-w-2xl">
                    <div className="bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-3">
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-400">
                            {message.timestamp.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                </div>
                <div className="ml-2 mt-1 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User size={16} className="text-blue-600" />
                    </div>
                </div>
            </div>
        );
    }

    // Assistant message
    return (
        <div className="flex justify-start">
            <div className="mr-2 mt-1 flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Compass size={16} className="text-white" />
                </div>
            </div>
            <div className="max-w-3xl flex-1 min-w-0">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    {/* Text message */}
                    {message.content && (
                        <TextBlock content={message.content} />
                    )}

                    {/* Render blocks */}
                    {message.blocks && message.blocks.map((block, idx) => renderBlock(block, idx))}

                    {/* Error state */}
                    {message.isError && !message.blocks?.length && (
                        <div className="text-sm text-red-600">
                            {message.content || 'Det oppstod en feil'}
                        </div>
                    )}
                </div>

                {/* Metadata footer */}
                <div className="flex items-center gap-3 mt-1 px-1">
                    <span className="text-xs text-gray-400">
                        {message.timestamp.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {message.executionTimeMs !== undefined && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock size={10} />
                            {message.executionTimeMs}ms
                        </span>
                    )}
                    {message.tokensUsed !== undefined && (
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                            <Zap size={10} />
                            {message.tokensUsed} tokens
                        </span>
                    )}
                    {message.cacheHit && (
                        <span className="flex items-center gap-1 text-xs text-green-500">
                            <Database size={10} />
                            cache
                        </span>
                    )}

                    {/* Save as report button — only on assistant messages with generated SQL */}
                    {message.generatedSQL && onSaveAsReport && (
                        <button
                            onClick={() => onSaveAsReport(message.generatedSQL!, userPrompt || '')}
                            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-50 px-2 py-0.5 rounded transition-colors ml-auto"
                            title="Lagre som rapport"
                        >
                            <FileBarChart size={11} />
                            Lagre som rapport
                        </button>
                    )}

                    {/* Pin to dashboard button */}
                    {message.generatedSQL && (
                        <button
                            onClick={handlePinToDashboard}
                            disabled={isPinning}
                            className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded transition-colors ${
                                pinSuccess
                                    ? 'text-green-600 bg-green-50'
                                    : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'
                            }`}
                            title="Pin til dashboard"
                        >
                            {pinSuccess ? (
                                <>
                                    <Check size={11} />
                                    Pinnet!
                                </>
                            ) : (
                                <>
                                    <Pin size={11} />
                                    {isPinning ? 'Pinner...' : 'Pin til dashboard'}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
